import { useEffect, useRef } from "react";
import "./FolhaColaboradores.css";
import { Input } from "../../Input";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select } from "../../select";
import { toast } from "react-toastify";
import { usePayroll } from "../../../hooks/usePayroll";
import { PageHeader } from "../../page-header";

const onlyNumbers = (value = "") => value.replace(/\D/g, "");

const numberSchema = z
  .string()
  .optional()
  .refine(
    (val) => !val || !isNaN(Number(val)),
    { message: "Valor inválido" }
  );


const createCollaboratorSchema = z.object({
  name: z.string(),
  email: z
    .string()
    .refine(
      (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      { message: "Email inválido" }
    ),
  phone: z.string(),
  taxNumber: z.string(),
  birthDate: z.string(),
  admissionDate: z.string(),
  employmentType: z.string(),
  jobTitle: z.string(),
  salary: z.string(),
  weeklyworkload: z.string(),
  department: z.string(),
  paymentMethod: z.string(),
  bank: z.string(),
  bankBranch: z.string(),
  bankAccount: z.string(),
  bankAccountDigit: z.string(),
  pixKey: z.string(),

  discounts: z
    .array(
      z.object({
        description: z.string(),
        fixedValue: numberSchema,
        percentageValue: numberSchema.refine(
          (val) => !val || Number(val) <= 100,
          { message: "Percentual não pode ser maior que 100%" }
        ),
      })
    )
    .optional(),
}).superRefine((data, ctx) => {
  const salary = Number(data.salary) || 0;
  const discounts = data.discounts || [];

  const totalDiscounts = discounts.reduce((acc, d) => {
    const fixed = Number(d.fixedValue) || 0;
    const perc = Number(d.percentageValue) || 0;
    return acc + fixed + (salary * perc) / 100;
  }, 0);

  if (totalDiscounts >= salary) {
    ctx.addIssue({
      path: ["discounts"],
      message: "Total de descontos não pode ser maior ou igual ao salário",
      code: z.ZodIssueCode.custom,
    });
  }
});

export default function FolhaColaboradores() {
  const { createCollaborator, loading } = usePayroll();

  const lastDiscountErrorRef = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createCollaboratorSchema),
    defaultValues: {
      discounts: [
        { description: "", fixedValue: "", percentageValue: "" },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "discounts",
  });

  const salary = Number(watch("salary")) || 0;
  const discounts = watch("discounts") || [];

  const totalDiscounts = discounts.reduce((acc, d) => {
    const fixed = Number(d.fixedValue) || 0;
    const perc = Number(d.percentageValue) || 0;
    return acc + fixed + (salary * perc) / 100;
  }, 0);

  const liquido = salary - totalDiscounts;

  useEffect(() => {
    if (totalDiscounts >= salary && salary > 0) {
      if (!lastDiscountErrorRef.current) {
        toast.error("Total de descontos não pode ser maior ou igual ao salário");
        lastDiscountErrorRef.current = true;
      }
    } else {
      lastDiscountErrorRef.current = false;
    }
  }, [totalDiscounts, salary]);

  useEffect(() => {
    const preventScroll = (e) => e.preventDefault();
    const inputs = document.querySelectorAll("input[type=number]");

    inputs.forEach((input) =>
      input.addEventListener("wheel", preventScroll, { passive: false })
    );

    return () => {
      inputs.forEach((input) =>
        input.removeEventListener("wheel", preventScroll)
      );
    };
  }, []);

  async function onSubmit(form) {
    await validatePayload(form);

    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      birthDate: form.birthDate,
      taxNumber: onlyNumbers(form.taxNumber),
      isInternalAccount: false,

      bankAccount: {
        bank: form.bank,
        bankBranch: form.bankBranch,
        bankAccount: form.bankAccount,
        bankAccountDigit: form.bankAccountDigit,
        pixKey: form.pixKey,
        paymentMethod: form.paymentMethod,
      },

      employment: {
        salary: form.salary,
        admissionDate: form.admissionDate,
        jobTitle: form.jobTitle,
        employmentType: form.employmentType,
        department: form.department,
        weeklyworkload: form.weeklyworkload,
      },

      discounts: form.discounts?.filter(
        (d) =>
          d.description &&
          (d.fixedValue || d.percentageValue)
      ),
    };

    try {
      await createCollaborator(payload);
      reset();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao salvar"
      );
    }
  }

  const validatePayload = async (form) => {
    const discountTotalValue = (form.discounts || []).reduce((acc, d) => {
      const fixed = Number(d.fixedValue) || 0;
      const perc = Number(d.percentageValue) || 0;
      return acc + fixed + (Number(form.salary || 0) * perc) / 100;
    }, 0);

    if (discountTotalValue > Number(form.salary || 0)) {
      throw new Error("Total de descontos não pode ser maior que o salário");
    }
  }; 

  return (
    <div className="collaborator-form-container">
      <div className="col-md-10">
        <div className="mt-4 mb-3">
          <PageHeader title="Novo Colaborador" pages={[{ name: 'Novo Colaborador', link: "/folha-pagamento/dashboard" }]} />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Dados pessoais</div>
            </div>

            <div className="mb-3">
              <Input label={"Nome completo"} type="text" placeholder="Ex: Maria Eduarda Santos" register={register('name')} />
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"CPF / CNPJ"} type="text" placeholder="CPF (CLT) ou CNPJ (PJ)" register={register("taxNumber", {
                  onChange: (e) =>
                    setValue("taxNumber", onlyNumbers(e.target.value)),
                })}
                />
              </div>
              <div className="mb-3">
                <Input label={"Data de Nascimento"} type="date" register={register('birthDate')} />
              </div>
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Email"} type="email" placeholder="email@xpaysystem.com" register={register('email')} />
              </div>
              <div className="mb-3">
                <Input label={"Telefone"} type="text" placeholder="(00) 00000-0000" register={register('phone')} />
              </div>
            </div>
          </div>

          {/* ================= Dados trabalhistas ================= */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Dados trabalhistas</div>
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Data de admissão"} type="date" register={register('admissionDate')} />
              </div>
              <div className="mb-3">
                <Select
                  className="select-block"
                  register={register('employmentType')}
                  options={[
                    { value: "CLT", label: "CLT" },
                    { value: "PJ", label: "PJ" },
                  ]}
                  label={"Tipo de Vínculo"}
                />
              </div>
            </div>
            <div className="mb-3">
              <Input label={"Cargo / Função"} type="text" placeholder="Ex: Analista de Operação" register={register('jobTitle')} />
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Salário base / Pró-labore"} type="number" id="baseGanho" placeholder="R$ 0,00" register={register('salary')} />
              </div>
              <div className="mb-3">
                <Select
                  label={"Jornada Semanal"}
                  register={register('weeklyworkload')}
                  options={[
                    { value: "40", label: "40h" },
                    { value: "48", label: "48h" },
                    { value: "52", label: "52h" },
                    { value: "60", label: "60h" },
                  ]}
                />

              </div>
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Departamento"} type="text" placeholder="Ex: Operações • Unidade 01" register={register('department')} />
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Dados bancários e de contato para pagamento</div>
            </div>
            <div className="mb-3">
              <Select
                label={"Forma de recebimento"}
                register={register('paymentMethod')}
                options={[
                  { value: "Transferência", label: "Transferência" },
                  { value: "Pix", label: "Pix" },
                ]}
              />
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Número do Banco"} type="number" placeholder="Ex: 260" register={register('bank')} />
              </div>
              <div className="mb-3">
                <Input label={"Agência"} type="number" placeholder="0000" register={register('bankBranch')} />
              </div>
            </div>
            <div className="row-2">
              <div className="mb-3">
                <Input label={"Conta"} type="number" placeholder="000000" register={register('bankAccount')} />
              </div>
              <div className="mb-3">
                <Input label={"Digito"} type="number" placeholder="0" maxLength={1} register={register('bankAccountDigit')} />
              </div>
            </div>
            <div className="mb-3">
              <Input label={"Chave Pix"} type="text" placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória" register={register('pixKey')} />
            </div>
          </div>
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Lançar descontos do colaborador</div>
            </div>
            <p className="subtitle" style={{ marginBottom: "6px" }}>
              Inclua descontos como transporte, adiantamento, convênios, etc. Os valores serão abatidos do ganho total deste colaborador.
            </p>
            <div style={{ marginTop: "18px", marginBottom: "4px", fontSize: "11px" }}>
              Descontos lançados
            </div>
            <div>
              {fields.map((field, index) => (
                <div className="discount-card d-flex flex-row gap-2" key={field.id}>
                  <div className="col-md-4">
                    <Input
                      placeholder="Descrição"
                      register={register(`discounts.${index}.description`)}
                    />
                  </div>

                  <div className="col-md-3">
                    <Input
                      placeholder="Valor fixo (R$)"
                      type="number"
                      step="0.01"
                      register={register(`discounts.${index}.fixedValue`)}
                      disabled={!!watch(`discounts.${index}.percentageValue`)}
                    />
                  </div>

                  <div className="col-md-3">
                    <Input
                      placeholder="% sobre salário"
                      type="number"
                      step="0.01"
                      register={register(`discounts.${index}.percentageValue`, {
                        onChange: (e) => {
                          const value = Number(e.target.value);

                          if (value > 100) {
                            setValue(`discounts.${index}.percentageValue`, "100");
                          }
                          if (value < 0) {
                            setValue(`discounts.${index}.percentageValue`, "0");
                          }
                        },
                      })}
                      disabled={!!watch(`discounts.${index}.fixedValue`)}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-outline danger justify-content-center"
                    onClick={() => remove(index)}
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn-outline"
              onClick={() =>
                append({
                  description: "",
                  fixedValue: "",
                  percentageValue: "",
                })
              }
            >
              Lançar mais descontos
            </button>

            <div className="discount-summary">
              <div>
                Total de descontos: <strong>R$ {totalDiscounts.toFixed(2)}</strong>
              </div>
              <div>
                Valor líquido: <strong>R$ {liquido.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <button className="confirm-button" disabled={loading}>
            {loading ? "Salvando..." : "Salvar colaborador"}
          </button>
        </form>
      </div>
    </div>
  );
}